import React, { useEffect, useImperativeHandle, useRef, forwardRef, useCallback } from 'react';
import { LayoutChangeEvent, Platform, ScrollView, ViewStyle } from 'react-native';

import {
  getCarouselMaxScrollLeft,
  getCarouselPageCount,
  getCarouselPageFromScrollLeft,
  getCarouselScrollLeftForPage,
} from './utils';

export interface HorizontalCarouselHandle {
  scrollToPage: (page: number, animated?: boolean) => void;
}

interface HorizontalCarouselProps {
  itemCount: number;
  scrollStep: number;
  onActivePageChange: (page: number, pageCount: number) => void;
  contentContainerStyle?: ViewStyle;
  children: React.ReactNode;
}

const SNAP_ANIMATION_MS = 380;

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;

// eslint-disable-next-line no-undef
const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest(
      'button, a, input, textarea, select, [role="button"], [data-carousel-ignore-drag]'
    )
  );
};

const WebHorizontalCarousel = forwardRef<HorizontalCarouselHandle, HorizontalCarouselProps>(
  ({ itemCount, scrollStep, onActivePageChange, contentContainerStyle, children }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftStartRef = useRef(0);
    const hasDraggedRef = useRef(false);
    const animationFrameRef = useRef<number>();
    const moveSamplesRef = useRef<Array<{ x: number; time: number }>>([]);
    const metricsRef = useRef({ pageCount: 1, maxPageIndex: 0, maxScrollLeft: 0 });
    const lastNotifiedPageRef = useRef<{ page: number; pageCount: number }>({
      page: 0,
      pageCount: 1,
    });
    const onActivePageChangeRef = useRef(onActivePageChange);

    onActivePageChangeRef.current = onActivePageChange;

    const notifyActivePageChange = useCallback((page: number, pageCount: number) => {
      const last = lastNotifiedPageRef.current;
      if (last.page === page && last.pageCount === pageCount) {
        return;
      }

      lastNotifiedPageRef.current = { page, pageCount };
      onActivePageChangeRef.current(page, pageCount);
    }, []);

    const cancelAnimation = () => {
      if (animationFrameRef.current) {
        // eslint-disable-next-line no-undef
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };

    const updateMetrics = useCallback(() => {
      const el = containerRef.current;
      if (!el) {
        return metricsRef.current;
      }

      const pageCount = getCarouselPageCount(el.clientWidth, itemCount, scrollStep);
      const maxPageIndex = Math.max(0, pageCount - 1);
      const maxScrollLeft = getCarouselMaxScrollLeft(el.scrollWidth, el.clientWidth);
      const currentPage = getCarouselPageFromScrollLeft(
        el.scrollLeft,
        scrollStep,
        maxPageIndex,
        maxScrollLeft
      );

      metricsRef.current = { pageCount, maxPageIndex, maxScrollLeft };
      notifyActivePageChange(currentPage, pageCount);

      return metricsRef.current;
    }, [itemCount, notifyActivePageChange, scrollStep]);

    const getScrollLeftForPage = useCallback(
      (pageIndex: number) => {
        const { maxScrollLeft } = metricsRef.current;
        return getCarouselScrollLeftForPage(pageIndex, scrollStep, maxScrollLeft);
      },
      [scrollStep]
    );

    const animateScrollLeft = useCallback(
      (
        el: HTMLDivElement,
        targetLeft: number,
        duration = SNAP_ANIMATION_MS,
        onComplete?: () => void
      ) => {
        cancelAnimation();
        el.style.scrollSnapType = 'none';

        const startLeft = el.scrollLeft;
        const distance = targetLeft - startLeft;
        const startTime = performance.now();

        const step = (now: number) => {
          const progress = Math.min((now - startTime) / duration, 1);
          el.scrollLeft = startLeft + distance * easeOutCubic(progress);

          if (progress < 1) {
            animationFrameRef.current = requestAnimationFrame(step);
            return;
          }

          el.scrollLeft = targetLeft;
          el.style.scrollSnapType = 'x proximity';
          animationFrameRef.current = undefined;
          onComplete?.();
        };

        animationFrameRef.current = requestAnimationFrame(step);
      },
      []
    );

    const syncActivePage = useCallback(() => {
      const el = containerRef.current;
      if (!el) {
        return;
      }

      const { maxPageIndex, maxScrollLeft, pageCount } = metricsRef.current;
      const page = getCarouselPageFromScrollLeft(
        el.scrollLeft,
        scrollStep,
        maxPageIndex,
        maxScrollLeft
      );
      notifyActivePageChange(page, pageCount);
    }, [notifyActivePageChange, scrollStep]);

    const scrollToPage = useCallback(
      (page: number, animated = true) => {
        const el = containerRef.current;
        if (!el) {
          return;
        }

        const metrics = updateMetrics();
        const nextPage = Math.min(Math.max(page, 0), metrics.maxPageIndex);
        const targetLeft = getScrollLeftForPage(nextPage);

        if (!animated) {
          cancelAnimation();
          el.style.scrollSnapType = 'none';
          el.scrollLeft = targetLeft;
          el.style.scrollSnapType = 'x proximity';
          notifyActivePageChange(nextPage, metrics.pageCount);
          return;
        }

        animateScrollLeft(el, targetLeft, SNAP_ANIMATION_MS, () => {
          notifyActivePageChange(nextPage, metrics.pageCount);
        });
      },
      [animateScrollLeft, getScrollLeftForPage, notifyActivePageChange, updateMetrics]
    );

    useImperativeHandle(ref, () => ({ scrollToPage }), [scrollToPage]);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) {
        return;
      }

      const getTargetPageFromRelease = (velocityPxPerMs: number) => {
        const { maxPageIndex, maxScrollLeft } = metricsRef.current;
        const projectedScrollLeft = el.scrollLeft - velocityPxPerMs * 220;
        const hasMeaningfulVelocity = Math.abs(velocityPxPerMs) > 0.25;

        if (hasMeaningfulVelocity) {
          return getCarouselPageFromScrollLeft(
            projectedScrollLeft,
            scrollStep,
            maxPageIndex,
            maxScrollLeft
          );
        }

        return getCarouselPageFromScrollLeft(
          el.scrollLeft,
          scrollStep,
          maxPageIndex,
          maxScrollLeft
        );
      };

      const getReleaseVelocity = () => {
        const samples = moveSamplesRef.current;
        if (samples.length < 2) {
          return 0;
        }

        const last = samples[samples.length - 1];
        const first = samples[Math.max(0, samples.length - 4)];
        const elapsed = last.time - first.time;

        if (elapsed <= 0) {
          return 0;
        }

        return (last.x - first.x) / elapsed;
      };

      const snapToNearest = () => {
        updateMetrics();
        const targetPage = getTargetPageFromRelease(getReleaseVelocity());
        animateScrollLeft(el, getScrollLeftForPage(targetPage), SNAP_ANIMATION_MS, () => {
          syncActivePage();
        });
      };

      const beginDrag = (pageX: number) => {
        cancelAnimation();
        updateMetrics();
        isDraggingRef.current = true;
        hasDraggedRef.current = false;
        startXRef.current = pageX;
        scrollLeftStartRef.current = el.scrollLeft;
        moveSamplesRef.current = [{ x: pageX, time: performance.now() }];
        el.style.cursor = 'grabbing';
        el.style.scrollSnapType = 'none';
      };

      const moveDrag = (pageX: number, preventDefault?: () => void) => {
        if (!isDraggingRef.current) {
          return;
        }

        preventDefault?.();

        const delta = pageX - startXRef.current;
        if (Math.abs(delta) > 4) {
          hasDraggedRef.current = true;
        }

        const maxScrollLeft = getCarouselMaxScrollLeft(el.scrollWidth, el.clientWidth);
        const nextScrollLeft = scrollLeftStartRef.current - delta;
        el.scrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScrollLeft);

        moveSamplesRef.current.push({ x: pageX, time: performance.now() });

        if (moveSamplesRef.current.length > 6) {
          moveSamplesRef.current.shift();
        }
      };

      const endDrag = () => {
        if (!isDraggingRef.current) {
          return;
        }

        isDraggingRef.current = false;
        el.style.cursor = 'grab';
        snapToNearest();
      };

      const onMouseDown = (event: MouseEvent) => {
        if (isInteractiveTarget(event.target)) {
          return;
        }

        beginDrag(event.pageX);
      };

      const onMouseMove = (event: MouseEvent) => {
        moveDrag(event.pageX, () => event.preventDefault());
      };

      // eslint-disable-next-line no-undef
      const onTouchStart = (event: TouchEvent) => {
        if (isInteractiveTarget(event.target) || event.touches.length !== 1) {
          return;
        }

        beginDrag(event.touches[0].pageX);
      };

      // eslint-disable-next-line no-undef
      const onTouchMove = (event: TouchEvent) => {
        if (event.touches.length !== 1) {
          return;
        }

        moveDrag(event.touches[0].pageX, () => event.preventDefault());
      };

      const onTouchEnd = () => {
        endDrag();
      };

      const onClickCapture = (event: MouseEvent) => {
        if (hasDraggedRef.current) {
          event.preventDefault();
          event.stopPropagation();
          hasDraggedRef.current = false;
        }
      };

      // eslint-disable-next-line no-undef
      const resizeObserver = new ResizeObserver(() => {
        updateMetrics();
      });

      resizeObserver.observe(el);
      updateMetrics();

      const onScroll = () => {
        syncActivePage();
      };

      el.addEventListener('scroll', onScroll, { passive: true });
      el.addEventListener('mousedown', onMouseDown);
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('click', onClickCapture, true);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', endDrag);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd);
      window.addEventListener('touchcancel', onTouchEnd);

      return () => {
        cancelAnimation();
        resizeObserver.disconnect();
        el.removeEventListener('scroll', onScroll);
        el.removeEventListener('mousedown', onMouseDown);
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('click', onClickCapture, true);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', endDrag);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('touchcancel', onTouchEnd);
      };
    }, [
      animateScrollLeft,
      getScrollLeftForPage,
      itemCount,
      scrollStep,
      scrollToPage,
      syncActivePage,
      updateMetrics,
    ]);

    return (
      <div
        ref={containerRef}
        className="report-generator-carousel"
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          cursor: 'grab',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x',
          scrollSnapType: 'x proximity',
          scrollBehavior: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          ...(contentContainerStyle as React.CSSProperties),
        }}>
        {children}
      </div>
    );
  }
);

WebHorizontalCarousel.displayName = 'WebHorizontalCarousel';

const NativeHorizontalCarousel = forwardRef<HorizontalCarouselHandle, HorizontalCarouselProps>(
  ({ itemCount, scrollStep, onActivePageChange, contentContainerStyle, children }, ref) => {
    const scrollRef = useRef<ScrollView>(null);
    const containerWidthRef = useRef(0);
    const metricsRef = useRef({ pageCount: 1, maxPageIndex: 0, maxScrollLeft: 0 });

    const updateMetrics = useCallback(
      (containerWidth: number, contentWidth?: number) => {
        const pageCount = getCarouselPageCount(containerWidth, itemCount, scrollStep);
        const maxPageIndex = Math.max(0, pageCount - 1);
        const maxScrollLeft = contentWidth
          ? getCarouselMaxScrollLeft(contentWidth, containerWidth)
          : maxPageIndex * scrollStep;

        metricsRef.current = { pageCount, maxPageIndex, maxScrollLeft };
        return metricsRef.current;
      },
      [itemCount, scrollStep]
    );

    const scrollToPage = useCallback(
      (page: number, animated = true) => {
        const { maxPageIndex, pageCount } = metricsRef.current;
        const nextPage = Math.min(Math.max(page, 0), maxPageIndex);
        const targetLeft = getCarouselScrollLeftForPage(
          nextPage,
          scrollStep,
          metricsRef.current.maxScrollLeft
        );

        scrollRef.current?.scrollTo({ x: targetLeft, y: 0, animated });
        onActivePageChange(nextPage, pageCount);
      },
      [onActivePageChange, scrollStep]
    );

    useImperativeHandle(ref, () => ({ scrollToPage }), [scrollToPage]);

    const handleLayout = (event: LayoutChangeEvent) => {
      containerWidthRef.current = event.nativeEvent.layout.width;
      const metrics = updateMetrics(containerWidthRef.current);
      onActivePageChange(0, metrics.pageCount);
    };

    const handleScrollEnd = (offsetX: number, contentWidth: number) => {
      const metrics = updateMetrics(containerWidthRef.current, contentWidth);
      const page = getCarouselPageFromScrollLeft(
        offsetX,
        scrollStep,
        metrics.maxPageIndex,
        metrics.maxScrollLeft
      );
      onActivePageChange(page, metrics.pageCount);
    };

    return (
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        decelerationRate="fast"
        snapToInterval={scrollStep}
        snapToAlignment="start"
        disableIntervalMomentum
        nestedScrollEnabled
        directionalLockEnabled
        scrollEventThrottle={16}
        onLayout={handleLayout}
        onMomentumScrollEnd={(event) =>
          handleScrollEnd(event.nativeEvent.contentOffset.x, event.nativeEvent.contentSize.width)
        }
        onScrollEndDrag={(event) =>
          handleScrollEnd(event.nativeEvent.contentOffset.x, event.nativeEvent.contentSize.width)
        }
        style={{ width: '100%', flexGrow: 0 }}
        contentContainerStyle={contentContainerStyle}>
        {children}
      </ScrollView>
    );
  }
);

NativeHorizontalCarousel.displayName = 'NativeHorizontalCarousel';

export const HorizontalCarousel =
  Platform.OS === 'web' ? WebHorizontalCarousel : NativeHorizontalCarousel;
